/**
 * Display names for custody actors (ticket_custody_events.actor_id = auth
 * user id) through the canonical person-name rule
 * (src/lib/people/personDisplayName.ts): the shop roster name, then the
 * professional identity, then the account display name — never an email.
 *
 * This used to read `profiles.display_name` FIRST; signup stores the email
 * there by default, so "Work complete" showed the technician's email even
 * though the roster held their name. An id with no real name stays absent and
 * the renderer falls back to the role.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { fetchPersonDisplayNames } from '@/lib/people/personDisplayName';

export function useCustodyActorNames(userIds: ReadonlyArray<string | null | undefined>, shopId?: string | null) {
  const ids = [...new Set(userIds.filter((x): x is string => typeof x === 'string' && x.length > 0))].sort();
  return useQuery({
    queryKey: ['custody-actor-names', shopId ?? null, ids.join('|')],
    enabled: ids.length > 0,
    retry: false,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Record<string, string>> => {
      try {
        return await fetchPersonDisplayNames(supabase, ids, shopId ?? null);
      } catch (e) {
        console.warn('[custody-actor-names] lookup failed (non-blocking)', e);
        return {};
      }
    },
  });
}
